import { useState } from 'react';

export interface Dimensions {
  height: number;
  width: number;
  length: number;
  weight: number;
}

export interface UseDimensionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (dims: Dimensions) => void;
  initialDimensions?: Dimensions | null;
}

export interface Fields {
  height: string;
  width: string;
  length: string;
  weight: string;
}

const EMPTY: Fields = { height: '', width: '', length: '', weight: '' };

const isPositiveInt = (v: string) => /^\d+$/.test(v.trim()) && parseInt(v, 10) > 0;

export const useDimensionsModal = (props: UseDimensionsModalProps) => {
  const [fields, setFields] = useState<Fields>(() => ({
    height: props.initialDimensions?.height ? String(props.initialDimensions.height) : '',
    width: props.initialDimensions?.width ? String(props.initialDimensions.width) : '',
    length: props.initialDimensions?.length ? String(props.initialDimensions.length) : '',
    weight: props.initialDimensions?.weight ? String(props.initialDimensions.weight) : '',
  }));
  const [touched, setTouched] = useState<Partial<Record<keyof Fields, boolean>>>({});

  const handleClose = () => {
    setFields(EMPTY);
    setTouched({});
    props.onClose();
  };

  const handleChange = (key: keyof Fields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleBlur = (key: keyof Fields) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const fieldError = (key: keyof Fields) => touched[key] && !isPositiveInt(fields[key]);

  const isValid = (Object.keys(EMPTY) as (keyof Fields)[]).every((k) => isPositiveInt(fields[k]));

  const handleConfirm = () => {
    setTouched({ height: true, width: true, length: true, weight: true });
    if (!isValid) return;
    props.onConfirm({
      height: parseInt(fields.height, 10),
      width: parseInt(fields.width, 10),
      length: parseInt(fields.length, 10),
      weight: parseInt(fields.weight, 10),
    });
    setFields(EMPTY);
    setTouched({});
  };

  return {
    fields,
    handleClose,
    handleChange,
    handleBlur,
    fieldError,
    isValid,
    handleConfirm,
  };
};
